/**
 * Shodan investigation collector.
 *
 * Queries the Shodan API for exposed device data. Supports two modes:
 * - Host lookup (free tier): looks up specific IPs extracted from keywords
 * - Search (paid membership): keyword + geo search for exposed devices
 *
 * The collector auto-detects which mode to use: if keywords contain IP
 * addresses, it does host lookups (free). Otherwise it attempts search
 * and falls back gracefully if the key lacks query credits.
 */

import { getApiBase } from '@/app/api-router';
import type { CollectionTask } from '../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_TIMEOUT_MS = 15_000;
const MAX_HOSTS = 100;
const IP_REGEX = /\b(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/g;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShodanHost {
  ip_str: string;
  port: number;
  org?: string;
  os?: string;
  product?: string;
  version?: string;
  vulns?: string[];
  country_code?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  asn?: string;
  isp?: string;
  domains?: string[];
  hostnames?: string[];
  timestamp?: string;
}

/** Host lookup returns a different shape — ports as top-level array. */
interface ShodanHostDetail {
  ip_str: string;
  org?: string;
  os?: string;
  country_code?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  asn?: string;
  isp?: string;
  domains?: string[];
  hostnames?: string[];
  vulns?: string[];
  ports?: number[];
  data?: Array<{
    port: number;
    product?: string;
    version?: string;
    transport?: string;
    timestamp?: string;
  }>;
}

export interface ShodanCollectionResult {
  rawText: string;
  hosts: ShodanHost[];
  total: number;
  query: string;
}

// ---------------------------------------------------------------------------
// IP extraction from keywords
// ---------------------------------------------------------------------------

function extractIPs(keywords: string[]): string[] {
  const text = keywords.join(' ');
  const matches = text.match(IP_REGEX);
  if (!matches) return [];
  // Deduplicate
  return Array.from(new Set(matches));
}

// ---------------------------------------------------------------------------
// Host text formatter
// ---------------------------------------------------------------------------

function formatHost(host: ShodanHost): string {
  const lines: string[] = [];

  lines.push(`IP: ${host.ip_str}:${host.port}`);
  if (host.org) lines.push(`Org: ${host.org}`);
  if (host.isp) lines.push(`ISP: ${host.isp}`);
  if (host.product) lines.push(`Product: ${host.product}`);
  if (host.os) lines.push(`OS: ${host.os}`);
  if (host.vulns && host.vulns.length > 0) lines.push(`Vulns: ${host.vulns.join(', ')}`);
  if (host.country_code) lines.push(`Country: ${host.country_code}`);
  if (host.city) lines.push(`City: ${host.city}`);
  if (host.asn) lines.push(`ASN: ${host.asn}`);
  if (host.domains && host.domains.length > 0) lines.push(`Domains: ${host.domains.join(', ')}`);

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Host lookup (free tier) — /shodan/host/{ip}
// ---------------------------------------------------------------------------

async function lookupHost(
  ip: string,
  baseUrl: string,
  timeoutMs: number,
): Promise<ShodanHost[]> {
  const url = `${baseUrl}/api/data/shodan?action=host&ip=${encodeURIComponent(ip)}`;

  const response = await globalThis.fetch(url, {
    headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!response.ok) return [];

  const detail = await response.json() as ShodanHostDetail;

  // Convert host detail to ShodanHost[] (one entry per service/port)
  if (detail.data && detail.data.length > 0) {
    return detail.data.map(svc => ({
      ip_str: detail.ip_str,
      port: svc.port,
      org: detail.org,
      os: detail.os,
      product: svc.product,
      version: svc.version,
      vulns: detail.vulns,
      country_code: detail.country_code,
      city: detail.city,
      latitude: detail.latitude,
      longitude: detail.longitude,
      asn: detail.asn,
      isp: detail.isp,
      domains: detail.domains,
      hostnames: detail.hostnames,
      timestamp: svc.timestamp,
    }));
  }

  // Fallback: no service data, just port list
  if (detail.ports && detail.ports.length > 0) {
    return detail.ports.map(port => ({
      ip_str: detail.ip_str,
      port,
      org: detail.org,
      os: detail.os,
      vulns: detail.vulns,
      country_code: detail.country_code,
      city: detail.city,
      latitude: detail.latitude,
      longitude: detail.longitude,
      asn: detail.asn,
      isp: detail.isp,
      domains: detail.domains,
      hostnames: detail.hostnames,
    }));
  }

  return [];
}

// ---------------------------------------------------------------------------
// Collector
// ---------------------------------------------------------------------------

/**
 * Collect exposed device data from Shodan.
 *
 * Strategy:
 * 1. Extract IP addresses from keywords → host lookups (free tier)
 * 2. If no IPs found, attempt search (needs paid membership)
 * 3. If search returns 403/401, return graceful message
 */
export async function collectFromShodan(
  task: CollectionTask,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<ShodanCollectionResult> {
  const query = task.query as Record<string, unknown>;
  const keywords = (query.keywords as string[]) ?? [];
  const baseUrl = getApiBase();

  // Strategy 1: If keywords contain IP addresses, do host lookups (free)
  const ips = extractIPs(keywords);
  if (ips.length > 0) {
    const lookupResults = await Promise.allSettled(
      ips.slice(0, 10).map(ip => lookupHost(ip, baseUrl, timeoutMs)),
    );

    const hosts: ShodanHost[] = [];
    for (const result of lookupResults) {
      if (result.status === 'fulfilled') {
        hosts.push(...result.value);
      }
    }

    const capped = hosts.slice(0, MAX_HOSTS);
    return {
      rawText: capped.length > 0
        ? capped.map(formatHost).join('\n\n---\n\n')
        : `No Shodan data found for IPs: ${ips.join(', ')}`,
      hosts: capped,
      total: capped.length,
      query: ips.join(', '),
    };
  }

  // Strategy 2: No IPs — attempt search (requires paid membership)
  const searchParts: string[] = keywords.filter(k => k.length > 0);
  const geoBounds = query.geographicBounds as { bbox?: [number, number, number, number] } | undefined;
  if (geoBounds?.bbox) {
    const [minLon, minLat, maxLon, maxLat] = geoBounds.bbox;
    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;
    const latDist = (maxLat - minLat) * 111;
    const lonDist = (maxLon - minLon) * 111 * Math.cos((centerLat * Math.PI) / 180);
    const radiusKm = Math.round(Math.max(latDist, lonDist) / 2);
    searchParts.push(`geo:${centerLat.toFixed(4)},${centerLon.toFixed(4)},${radiusKm}`);
  }

  const shodanFilters = query.shodanFilters as string | undefined;
  if (shodanFilters) searchParts.push(shodanFilters);

  const queryString = searchParts.join(' ');
  if (!queryString) {
    return {
      rawText: 'Shodan query requires IP addresses or keywords. Provide IPs for free-tier lookups.',
      hosts: [],
      total: 0,
      query: '',
    };
  }

  const url = `${baseUrl}/api/data/shodan?action=search&query=${encodeURIComponent(queryString)}`;
  const response = await globalThis.fetch(url, {
    headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(timeoutMs),
  });

  // Search requires paid membership — fail gracefully
  if (response.status === 401 || response.status === 403) {
    return {
      rawText: 'Shodan search requires a paid membership ($49 one-time). Free tier supports IP host lookups only — include specific IP addresses in the investigation keywords.',
      hosts: [],
      total: 0,
      query: queryString,
    };
  }

  if (!response.ok) {
    throw new Error(`Shodan API returned ${response.status}: ${response.statusText}`);
  }

  const data = await response.json() as { matches?: ShodanHost[]; total?: number };
  const hosts = (data.matches ?? []).slice(0, MAX_HOSTS);

  return {
    rawText: hosts.length > 0
      ? hosts.map(formatHost).join('\n\n---\n\n')
      : 'No hosts found matching the query.',
    hosts,
    total: data.total ?? hosts.length,
    query: queryString,
  };
}
